import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { CustomSelect } from '../../shared/components/custom';
import localApiAdapter from '../../core/services/localApiAdapter';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import {
  mapAccountingToReconciliationEntry,
  mapBankStatementToReconciliationEntry,
} from './bankingApiMappers';
import {
  BuildingLibraryIcon,
  LinkIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  DocumentArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ChevronDoubleRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const ManualReconciliationPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.manualReconciliationPage.${key}`, opts);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const accountIdFromUrl = searchParams.get('account');

  const [selectedAccount, setSelectedAccount] = useState(accountIdFromUrl || null);
  const [selectedBankEntry, setSelectedBankEntry] = useState(null);
  const [selectedAccountingEntry, setSelectedAccountingEntry] = useState(null);
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const [accountingSearchTerm, setAccountingSearchTerm] = useState('');
  const [notes, setNotes] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastReconciliation, setLastReconciliation] = useState(null);

  const [bankAccounts, setBankAccounts] = useState([]);

  const formatBankAccountLabel = useCallback((account) => {
    if (!account) return '';
    const bank = account.bank_name || account.bank || account.bankName || '';
    const name = account.account_name || account.name || '';
    const num = account.account_number || account.accountNumber || '';
    const left = [bank, name].filter(Boolean).join(' - ');
    if (left && num) return `${left} (${num})`;
    if (left) return left;
    if (num) return String(num);
    return p('account_fallback', { id: account.id ?? '?' });
  }, [p]);

  const loadBankAccounts = useCallback(async () => {
    try {
      const data = await localApiAdapter.get('/banking/accounts');
      setBankAccounts(normalizeAndSyncBankAccounts(data));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  }, []);

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  const [unreconciledData, setUnreconciledData] = useState({ accountingEntries: [], bankEntries: [] });
  const [isLoadingUnreconciled, setIsLoadingUnreconciled] = useState(false);

  const refetchUnreconciled = useCallback(async () => {
    if (!selectedAccount) return;
    setIsLoadingUnreconciled(true);
    try {
      const [statements, accountingRows] = await Promise.all([
        localApiAdapter.get('/banking/statements', {
          account_id: selectedAccount,
          reconciled: 'false',
        }),
        localApiAdapter.get('/banking/accounting', { status: 'unreconciled' }),
      ]);
      const bankEntries = (Array.isArray(statements) ? statements : []).map(mapBankStatementToReconciliationEntry);
      const accountingEntries = (Array.isArray(accountingRows) ? accountingRows : [])
        .filter((entry) => String(entry.account_id) === String(selectedAccount))
        .map(mapAccountingToReconciliationEntry);
      setUnreconciledData({ accountingEntries, bankEntries });
    } catch (error) {
      console.error('Error loading unreconciled entries:', error);
      toast.error(error.message || p('toast_error'));
      setUnreconciledData({ accountingEntries: [], bankEntries: [] });
    } finally {
      setIsLoadingUnreconciled(false);
    }
  }, [selectedAccount, p]);
  
  useEffect(() => {
    if (selectedAccount) {
      refetchUnreconciled();
    } else {
      setUnreconciledData({ accountingEntries: [], bankEntries: [] });
    }
  }, [selectedAccount, refetchUnreconciled]);

  const [isCreatingReconciliation, setIsCreatingReconciliation] = useState(false);

  const createReconciliation = async (reconciliationData) => {
    setIsCreatingReconciliation(true);
    try {
      const periodEnd = new Date();
      const periodStart = new Date();
      periodStart.setMonth(periodStart.getMonth() - 1);

      const session = await localApiAdapter.post('/banking/reconciliation', {
        account_id: Number(reconciliationData.bankAccountId),
        period_start: periodStart.toISOString().split('T')[0],
        period_end: periodEnd.toISOString().split('T')[0],
        opening_balance: 0,
        closing_balance: 0,
        statement_balance: 0,
        book_balance: 0,
        notes: reconciliationData.notes || null,
      });

      const reconciliationId = session?.id ?? session?.data?.id;
      if (!reconciliationId) {
        throw new Error(p('toast_error'));
      }

      const matchResult = await localApiAdapter.post(`/banking/reconciliation/${reconciliationId}/manual-match`, {
        bank_statement_id: reconciliationData.bankEntryId,
        accounting_entry_id: reconciliationData.accountingEntryId,
        notes: reconciliationData.notes || null,
      });

      toast.success(p('toast_success'));
      setLastReconciliation({
        id: reconciliationId,
        amount: matchResult?.amount ?? 0,
        ...reconciliationData,
      });
      setShowSuccessModal(true);

      setSelectedBankEntry(null);
      setSelectedAccountingEntry(null);
      setNotes('');

      await refetchUnreconciled();

      return matchResult;
    } catch (error) {
      toast.error(error.message || p('toast_error'));
      console.error('Error:', error);
      throw error;
    } finally {
      setIsCreatingReconciliation(false);
    }
  };

  // Filtrer les mouvements bancaires
  const filteredBankEntries = unreconciledData?.bankEntries 
    ? unreconciledData.bankEntries.filter(entry => {
        const searchTermLower = bankSearchTerm.toLowerCase();
        return entry.reference?.toLowerCase().includes(searchTermLower) ||
               entry.description?.toLowerCase().includes(searchTermLower) ||
               String(entry.amount).includes(searchTermLower) ||
               (entry.date && String(entry.date).includes(searchTermLower));
      })
    : [];

  // Filtrer les �critures comptables
  const filteredAccountingEntries = unreconciledData?.accountingEntries 
    ? unreconciledData.accountingEntries.filter(entry => {
        const searchTermLower = accountingSearchTerm.toLowerCase();
        return entry.reference?.toLowerCase().includes(searchTermLower) ||
               entry.description?.toLowerCase().includes(searchTermLower) ||
               String(entry.amount).includes(searchTermLower) ||
               (entry.date && String(entry.date).includes(searchTermLower));
      })
    : [];

  // G�rer la S�lection du compte bancaire
  const handleAccountChange = (e) => {
    const accountId = e.target.value;
    setSelectedAccount(accountId);
    setSelectedBankEntry(null);
    setSelectedAccountingEntry(null);
  };

  // Effectuer un rapprochement
  const handleReconcile = () => {
    if (!selectedAccount || !selectedBankEntry || !selectedAccountingEntry) {
      toast.error(p('toast_select_entries'));
      return;
    }

    createReconciliation({
      bankAccountId: selectedAccount,
      bankEntryId: selectedBankEntry.id,
      accountingEntryId: selectedAccountingEntry.id,
      notes: notes
    });
  };

  // Formater les montants
  const formatAmount = (amount) => formatBrandMoney(amount);

  // Fonction pour v�rifier si les montants correspondent
  const amountsMatch = useCallback(() => {
    if (!selectedBankEntry || !selectedAccountingEntry) return false;
    return selectedBankEntry.amount === selectedAccountingEntry.amount;
  }, [selectedBankEntry, selectedAccountingEntry]);

  // Fonction pour obtenir le compte s�lectionn�
  const getSelectedAccountName = () => {
    if (!selectedAccount || !bankAccounts) return '';
    const account = bankAccounts.find(
      (acc) => String(acc.id) === String(selectedAccount)
    );
    return account ? formatBankAccountLabel(account) : '';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">{p('title')}</h1>
        <p className="text-white/60 mt-2">{p('subtitle')}</p>
      </div>

      <div className="bg-white/5 rounded-xl border border-white/20 p-6 overflow-hidden">
        <h2 className="text-xl font-semibold text-white mb-4">{p('select_account_title')}</h2>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-2/3">
            <CustomSelect
              value={selectedAccount || ''}
              onChange={(value) => {
                handleAccountChange({ target: { value } });
              }}
              options={[
                { value: '', label: p('select_account_placeholder') },
                ...bankAccounts.map((account) => ({
                  value: String(account.id),
                  label: formatBankAccountLabel(account),
                })),
              ]}
              placeholder={p('select_account_placeholder')}
              searchable={true}
            />
          </div>
          <button
            onClick={() => refetchUnreconciled()}
            disabled={!selectedAccount}
            className="bg-blue-600/80 hover:bg-blue-700 disabled:bg-white/10 disabled:text-white/50 text-white px-4 py-3 rounded-xl flex items-center justify-center gap-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            <span>{p('refresh_data')}</span>
          </button>
        </div>
      </div>

      {selectedAccount && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <DocumentArrowDownIcon className="h-5 w-5 text-blue-400" />
                {p('bank_movements_title')}
                {unreconciledData?.bankEntries && (
                  <span className="text-sm bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
                    {unreconciledData.bankEntries.length}
                  </span>
                )}
              </h3>
              <div className="mt-4 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_bank_placeholder')}
                  value={bankSearchTerm}
                  onChange={(e) => setBankSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {isLoadingUnreconciled ? (
              <div className="p-6 flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : !unreconciledData?.bankEntries || filteredBankEntries.length === 0 ? (
              <div className="p-6 text-center text-white/60">
                <p>{p('no_bank_movements')}</p>
              </div>
            ) : (
              <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                <ul className="divide-y divide-white/10">
                  {filteredBankEntries.map((entry) => (
                    <li
                      key={entry.id}
                      onClick={() => setSelectedBankEntry(entry)}
                      className={`px-6 py-4 hover:bg-white/5 cursor-pointer transition-colors ${
                        selectedBankEntry?.id === entry.id ? 'bg-blue-900/30' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-white">{entry.description}</p>
                          <p className="text-sm text-white/60 mt-1">
                            {entry.date}{' '}
                            {entry.reference ? `- ${p('ref_prefix', { ref: entry.reference })}` : ''}
                          </p>
                        </div>
                        <div className={`font-semibold ${entry.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatAmount(entry.amount)} {p('currency_da')}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex flex-col bg-white/5 rounded-xl border border-white/20 p-6 overflow-hidden">
            <h3 className="text-lg font-semibold text-white mb-4">{p('reconciliation_panel_title')}</h3>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
              <h4 className="text-sm text-white/60 mb-1">{p('selected_account')}</h4>
              <p className="text-white font-medium">{getSelectedAccountName()}</p>
            </div>
            <div className="flex-grow space-y-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <h4 className="text-sm text-white/60 mb-2">{p('bank_movement')}</h4>
                {selectedBankEntry ? (
                  <div>
                    <p className="text-white font-medium">{selectedBankEntry.description}</p>
                    <p className="text-sm text-white/80 mt-1">
                      {selectedBankEntry.date} - {selectedBankEntry.reference || p('no_reference')}
                    </p>
                    <p className={`text-lg font-semibold mt-2 ${selectedBankEntry.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatAmount(selectedBankEntry.amount)} {p('currency_da')}
                    </p>
                  </div>
                ) : (
                  <p className="text-white/50 italic">{p('no_bank_movement_selected')}</p>
                )}
              </div>
              <div className="flex justify-center">
                <div className="p-2 rounded-full bg-white/5">
                  {amountsMatch() ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  ) : (
                    <ChevronDoubleRightIcon className="h-6 w-6 text-white/50" />
                  )}
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <h4 className="text-sm text-white/60 mb-2">{p('accounting_entry')}</h4>
                {selectedAccountingEntry ? (
                  <div>
                    <p className="text-white font-medium">{selectedAccountingEntry.description}</p>
                    <p className="text-sm text-white/80 mt-1">
                      {selectedAccountingEntry.date} - {selectedAccountingEntry.reference}
                    </p>
                    <p className={`text-lg font-semibold mt-2 ${selectedAccountingEntry.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatAmount(selectedAccountingEntry.amount)} {p('currency_da')}
                    </p>
                  </div>
                ) : (
                  <p className="text-white/50 italic">{p('no_accounting_entry_selected')}</p>
                )}
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <label className="block w-full text-sm text-white/60 mb-2 font-medium text-right">{p('notes_optional')}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={p('notes_placeholder')}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 min-h-[80px]"
                />
              </div>
            </div>
            <button
              onClick={handleReconcile}
              disabled={!selectedBankEntry || !selectedAccountingEntry || isCreatingReconciliation}
              className="mt-6 py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:from-blue-600 hover:to-blue-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCreatingReconciliation ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{p('processing')}</span>
                </>
              ) : (
                <>
                  <LinkIcon className="h-5 w-5" />
                  <span>{p('reconcile_button')}</span>
                </>
              )}
            </button>
          </div>

          <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <DocumentMagnifyingGlassIcon className="h-5 w-5 text-purple-400" />
                {p('accounting_entries_title')}
                {unreconciledData?.accountingEntries && (
                  <span className="text-sm bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
                    {unreconciledData.accountingEntries.length}
                  </span>
                )}
              </h3>
              <div className="mt-4 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_accounting_placeholder')}
                  value={accountingSearchTerm}
                  onChange={(e) => setAccountingSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {isLoadingUnreconciled ? (
              <div className="p-6 flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : !unreconciledData?.accountingEntries || filteredAccountingEntries.length === 0 ? (
              <div className="p-6 text-center text-white/60">
                <p>{p('no_accounting_entries')}</p>
              </div>
            ) : (
              <div className="overflow-y-auto" style={{ maxHeight: '500px' }}>
                <ul className="divide-y divide-white/10">
                  {filteredAccountingEntries.map((entry) => (
                    <li
                      key={entry.id}
                      onClick={() => setSelectedAccountingEntry(entry)}
                      className={`px-6 py-4 hover:bg-white/5 cursor-pointer transition-colors ${
                        selectedAccountingEntry?.id === entry.id ? 'bg-purple-900/30' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-white">{entry.description}</p>
                          <p className="text-sm text-white/60 mt-1">
                            {entry.date} - {p('ref_prefix', { ref: entry.reference })}
                          </p>
                          <p className="text-xs text-white/40 mt-0.5">
                            {entry.account} - {entry.accountName}
                          </p>
                        </div>
                        <div className={`font-semibold ${entry.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatAmount(entry.amount)} {p('currency_da')}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {showSuccessModal && lastReconciliation && ReactDOM.createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setShowSuccessModal(false)} role="presentation" />
          <div
            className="relative w-full max-w-md z-10 p-8 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.35), 0 0 60px rgba(59, 130, 246, 0.15)' }}
          >
            <button
              className="absolute right-4 top-4 text-white/50 hover:text-white"
              onClick={() => setShowSuccessModal(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-10 w-10 text-green-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white text-center mb-4">{p('success_title')}</h2>
            <div className="bg-white/5 rounded-xl p-4 border border-white/20 mb-4">
              <h4 className="text-sm text-white/60 mb-1">{p('reference_label')}</h4>
              <p className="text-white font-medium">{lastReconciliation.id}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/20 mb-4">
              <h4 className="text-sm text-white/60 mb-1">{p('reconciled_amount')}</h4>
              <p className={`text-lg font-semibold ${lastReconciliation.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatAmount(lastReconciliation.amount)} {p('currency_da')}
              </p>
            </div>
            <div className="flex justify-between mt-6">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
              >
                {p('close')}
              </button>
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  refetchUnreconciled();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <ArrowPathIcon className="h-5 w-5" />
                <span>{p('refresh_data')}</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ManualReconciliationPage; 


